import { DisplayTheme, DisplayThemeValueSet } from '../../types';
import { BASE_COLORS, SHADOWS, U_ALPHA_DARK, U_ALPHA_LIGHT } from './baseColors';
import { BASE_COMPONENTS, BaseComponents } from './baseComponents';
import { BOOKWISE_COMPONENTS, BookwiseComponents } from './bookwiseComponents';
import { FUNCTIONAL_COLORS, FunctionalThemeColors } from './functionalColors';

type BookwiseComponentKeys = {
  [K in keyof BookwiseComponents]: ExtractVal<BookwiseComponents[K]>;
};

type ExtractVal<T> = T extends DisplayThemeValueSet<infer V> ? V : never;

/** Sentinel for "use React Native system default font" (Reader). */
export const READER_DEFAULT_FONT_FAMILY = 'default';

/** Default UI font family for Bookwise app (Albert Sans). */
export const BOOKWISE_UI_FONT_FAMILY = 'AlbertSans-Regular';

const FONT_FAMILY: DisplayThemeValueSet<string> = {
  [DisplayTheme.Light]: READER_DEFAULT_FONT_FAMILY,
  [DisplayTheme.Dark]: READER_DEFAULT_FONT_FAMILY,
  bookwise: {
    [DisplayTheme.Light]: BOOKWISE_UI_FONT_FAMILY,
    [DisplayTheme.Dark]: BOOKWISE_UI_FONT_FAMILY,
  },
};

export type Theme = typeof BASE_COLORS &
  typeof U_ALPHA_LIGHT &
  typeof SHADOWS &
  {
    [K in keyof FunctionalThemeColors]: string;
  } &
  {
    bookwise: BookwiseComponentKeys;
  } &
  {
    type: DisplayTheme;
  } &
  {
    [K in keyof BaseComponents]: ExtractVal<BaseComponents[K]>;
  } &
  { fontFamily: string; };

type ThemeVariantOptions = {
  isHighContrast: boolean;
  isBookwise: boolean;
};

function extractValueFromValueSet<Val>(valueSet: DisplayThemeValueSet<Val>, themeType: DisplayTheme, options: ThemeVariantOptions): Val {
  if (options.isHighContrast) {
    // if we have a high contrast value for the given display theme, return that
    if (options.isBookwise && valueSet.bookwiseHighContrast?.[themeType]) {
      const valToReturn = valueSet.bookwiseHighContrast[themeType];
      if (valToReturn !== undefined) {
        return valToReturn;
      }
    } else if (valueSet.highContrast !== undefined) {
      const valToReturn = valueSet.highContrast[themeType];
      if (valToReturn !== undefined) {
        return valToReturn;
      }
    }
  }
  if (options.isBookwise) {
    // if we have a bookwise override for the given display theme, return that
    if (valueSet.bookwise !== undefined) {
      const valToReturn = valueSet.bookwise[themeType];
      if (valToReturn !== undefined) {
        return valToReturn;
      }
    }
  }
  return valueSet[themeType];
}

type ExtractedTheme<T> = {
  [K in keyof T]: ExtractVal<T[K]>;
};


/*
  Takes in a set of themed values (like FUNCTIONAL_COLORS or BASE_COMPONENTS), and a theme type
  and returns just the values for the given set of theme + option combinations
  eg. if you pass a theme with a key like:
  text: {
    [DisplayTheme.Light]: BASE_COLORS.neutral20,
    [DisplayTheme.Dark]: BASE_COLORS.neutral95,
    highContrast: {
      [DisplayTheme.Light]: 'black',
      [DisplayTheme.Dark]: 'white',
    },
    bookwise: {
      [DisplayTheme.Light]: BASE_COLORS.stone20,
      [DisplayTheme.Dark]: BASE_COLORS.stone90,
    },
  },

  and themeType Dark + bookwise

  you will get { text: BASE_COLORS.stone90 }
 */
function extractTheme<T extends { [key: string]: DisplayThemeValueSet<unknown>; }>(
  themeValuesSet: T,
  themeType: DisplayTheme,
  options: ThemeVariantOptions,
): ExtractedTheme<T> {

  const entries = Object.entries(themeValuesSet).map(([key, valueSet]) => {
    const extracted = extractValueFromValueSet(
      valueSet,
      themeType,
      options,
    );
    return [key, extracted];
  });

  return Object.fromEntries(entries) as ExtractedTheme<T>;
}

const generateTheme = (displayThemeType: DisplayTheme, options: ThemeVariantOptions): Theme => {
  const alphaTheme = displayThemeType === DisplayTheme.Dark ? U_ALPHA_DARK : U_ALPHA_LIGHT;

  const extractedFunctionalColors = extractTheme(FUNCTIONAL_COLORS, displayThemeType, options);
  const extractedBaseComponents = extractTheme(BASE_COMPONENTS, displayThemeType, options);
  const extractedBookwiseComponents = extractTheme(BOOKWISE_COMPONENTS, displayThemeType, options);

  return {
    type: displayThemeType,
    ...BASE_COLORS,
    ...alphaTheme,
    ...SHADOWS,
    ...extractedFunctionalColors,
    ...extractedBaseComponents,
    bookwise: {
      ...extractedBookwiseComponents,
    },
    fontFamily: extractValueFromValueSet(FONT_FAMILY, displayThemeType, options),
  };
};

export const LIGHT_THEME: Theme = generateTheme(DisplayTheme.Light, { isBookwise: false, isHighContrast: false });

export const DARK_THEME: Theme = generateTheme(DisplayTheme.Dark, { isBookwise: false, isHighContrast: false });

export const BOOKWISE_LIGHT_THEME_OVERRIDES: Theme = generateTheme(DisplayTheme.Light, { isBookwise: true, isHighContrast: false });
export const BOOKWISE_DARK_THEME_OVERRIDES: Theme = generateTheme(DisplayTheme.Dark, { isBookwise: true, isHighContrast: false });

export const HIGH_CONTRAST_LIGHT_THEME: Theme = generateTheme(DisplayTheme.Light, { isBookwise: false, isHighContrast: true });
export const HIGH_CONTRAST_DARK_THEME: Theme = generateTheme(DisplayTheme.Dark, { isBookwise: false, isHighContrast: true });
const BOOKWISE_HIGH_CONTRAST_LIGHT_THEME = generateTheme(DisplayTheme.Light, { isBookwise: true, isHighContrast: true });
const BOOKWISE_HIGH_CONTRAST_DARK_THEME = generateTheme(DisplayTheme.Dark, { isBookwise: true, isHighContrast: true });

export const THEMES = {
  [DisplayTheme.Light]: LIGHT_THEME,
  [DisplayTheme.Dark]: DARK_THEME,
};

export const BOOKWISE_THEMES = {
  [DisplayTheme.Light]: BOOKWISE_LIGHT_THEME_OVERRIDES,
  [DisplayTheme.Dark]: BOOKWISE_DARK_THEME_OVERRIDES,
};

export const INVERSE_THEMES = {
  [DisplayTheme.Light]: DARK_THEME,
  [DisplayTheme.Dark]: LIGHT_THEME,
};

export const BOOKWISE_INVERSE_THEMES = {
  [DisplayTheme.Light]: BOOKWISE_DARK_THEME_OVERRIDES,
  [DisplayTheme.Dark]: BOOKWISE_LIGHT_THEME_OVERRIDES,
};


export const HIGH_CONTRAST_THEMES = {
  [DisplayTheme.Light]: HIGH_CONTRAST_LIGHT_THEME,
  [DisplayTheme.Dark]: HIGH_CONTRAST_DARK_THEME,
};

export const BOOKWISE_HIGH_CONTRAST_THEMES = {
  [DisplayTheme.Light]: BOOKWISE_HIGH_CONTRAST_LIGHT_THEME,
  [DisplayTheme.Dark]: BOOKWISE_HIGH_CONTRAST_DARK_THEME,
};

export const Spacing = {
  baseHorizontalMargin: 16,
  readerHeaderHeight: 60,
  readerBottomPadding: 200,
  maxDocumentWidth: 1024,
  minTabletDocumentWidth: 350,
  bookwiseCoverMargin: 12,
  bookwiseHomeMaxWidth: 620,
};

export const Timing = {
  readingViewBannerHide: 400,
};

export const ThemeCSSMap = {
  [DisplayTheme.Dark]: 'theme--dark',
  [DisplayTheme.Light]: 'theme--light',
};

export const getInverseTheme = (theme: Theme): Theme => {
  if (theme.type === DisplayTheme.Dark) {
    return THEMES[DisplayTheme.Light];
  } else {
    return THEMES[DisplayTheme.Dark];
  }
};
